import { SuggestedQuestion } from './actions';

export const FLOW_KEY_DCM = 'dcm';
export const FLOW_KEY_FEEDBACK = 'feedback';
export const MENTION_LABEL = '@chuckNorris';

// One rotating empty-chat greeting per open — shared by the desktop modal and
// the mobile full-screen surface (rendered by AskAicaGreeting).
export const ASK_AICA_GREETINGS: string[] = [
    'What would you like to explore today?',
    'Ready to unlock business insights?',
    "Let's dive into the data together",
    'Your AICA-powered business intelligence awaits',
    'Ready to discover what the data reveals?',
    "Let's transform your questions into insights",
];

// Portfolio-level Ask AICA (no single company in view): the mobile chat's
// "✦ Suggestions" strip, same as the company report's mobile Ask AICA.
export const PORTFOLIO_SUGGESTED_QUESTIONS: SuggestedQuestion[] = [
    { id: 'portfolio-attention', text: 'Which companies need attention?' },
    { id: 'portfolio-performance', text: "Summarise my portfolio's performance" },
    { id: 'portfolio-risk', text: 'Which companies have the highest risk?' },
    { id: 'portfolio-red-flags', text: 'Show companies with recent red flags' },
];

// Mixpanel `source` value for a suggestion tapped from the "✦ Suggestions" strip
// (mobile Ask AICA and the Portfolio chat's mobile layout).
export const SUGGESTIONS_STRIP_SOURCE = 'mobile_suggestions_strip';

export const DEFAULT_SUGGESTED_QUESTIONS: SuggestedQuestion[] = [
    {
        id: '1',
        text: 'Generate One Pager',
        flowKey: 'one_pager',
    },
    {
        id: '4',
        text: 'Generate One Pager as docx',
        hint: '(takes ~7-8 mins)',
        flowKey: 'one_pager_docx',
    },
    {
        id: '3',
        display: 'What debt works for this company?',
        text: 'Give me the debt recommendations for this company and be very thorough with using all the relevant data as needed?',
    },
    {
        id: '2',
        text: 'What are the best-suited lenders for this company?',
        flowKey: FLOW_KEY_DCM,
    },
    // Product-scoped, lender-agnostic pill: agentInput carries the product only —
    // omitting lender_org_id is what selects Athena's any-lender path. Another
    // product is one more entry here and nothing else.
    {
        id: '5',
        text: 'Can any lender do Purchase Invoice Discounting?',
        flowKey: FLOW_KEY_DCM,
        agentInput: { product_id: 'PURCHASE_INVOICE_DISCOUNTING', product_name: 'Purchase Invoice Discounting' },
    },
];

export enum MessageType {
    USER = 'user',
    ASSISTANT = 'assistant',
}

export const COPY_TARGET = {
    FULL_CHAT: 'fullChat',
} as const;

const namespace = 'ASK_AICA_';

/** Interval in ms for polling the org conversation list while the AskAica modal is open. */
export const CONVERSATION_LIST_POLL_MS = 25000;

// Kept in step with $portfolio-chat-fade in AskAicaV2.module.scss and asserted by
// AskAicaV2.test.tsx's parity check.
export const PORTFOLIO_CHAT_FADE_MS = 200;

// The chat's mobile layout (sidebar drawer, hidden Right Panel) is CSS `@include sm`,
// so its JS "mobile" check must use the same width — $breakpoints 'sm' in _breakpoints.scss,
// asserted by AskAicaV2.test.tsx's parity check.
export const ASK_AICA_MOBILE_BREAKPOINT = 481;

export const ASK_AICA_CONSTANTS = {
    // Chat functionality constants
    INITIALISE_MESSAGE_THREAD: `${namespace}INITIALISE_MESSAGE_THREAD`,
    ADD_MESSAGE: `${namespace}ADD_MESSAGE`,
    UPDATE_MESSAGE: `${namespace}UPDATE_MESSAGE`,
    REMOVE_MESSAGES: `${namespace}REMOVE_MESSAGES`,
    CLEAR_MESSAGES: `${namespace}CLEAR_MESSAGES`,
    SET_IS_LOADING: `${namespace}SET_IS_LOADING`,
    SET_IS_LOADING_CHAT_HISTORY: `${namespace}SET_IS_LOADING_CHAT_HISTORY`,
    SET_IS_CLEARING_CHAT: `${namespace}SET_IS_CLEARING_CHAT`,
    SET_ERROR: `${namespace}SET_ERROR`,
    SET_REASONING: `${namespace}SET_REASONING`,
    RESET_REASONING: `${namespace}RESET_REASONING`,
    UPDATE_REASONING: `${namespace}UPDATE_REASONING`,

    // UI-related constants
    SET_ASK_AICA_OPEN: `${namespace}SET_ASK_AICA_OPEN`,
    SET_ASK_AICA_RIGHT_PANEL_MODE: `${namespace}SET_ASK_AICA_RIGHT_PANEL_MODE`,

    // Global suggested questions constants
    SET_GLOBAL_SUGGESTED_QUESTIONS: `${namespace}SET_GLOBAL_SUGGESTED_QUESTIONS`,
    UPDATE_GLOBAL_SUGGESTED_QUESTIONS: `${namespace}UPDATE_GLOBAL_SUGGESTED_QUESTIONS`,

    // Library constants
    SET_LIBRARY_LOADING: `${namespace}SET_LIBRARY_LOADING`,
    SET_LIBRARY_FILES: `${namespace}SET_LIBRARY_FILES`,
    ADD_LIBRARY_FILES: `${namespace}ADD_LIBRARY_FILES`,

    // Conversation list constants
    SET_CONVERSATIONS: `${namespace}SET_CONVERSATIONS`,
    SET_LOADING_CONVERSATIONS: `${namespace}SET_LOADING_CONVERSATIONS`,
    SET_SELECTED_CONVERSATION: `${namespace}SET_SELECTED_CONVERSATION`,
    RESET_CHAT_VIEW: `${namespace}RESET_CHAT_VIEW`,
    SET_PENDING_NEW_CONVERSATION: `${namespace}SET_PENDING_NEW_CONVERSATION`,
    SET_SELECTED_CONVERSATION_IS_DELETED: `${namespace}SET_SELECTED_CONVERSATION_IS_DELETED`,
};

// Ask AICA "source" value for the leads panel's own conversations (Conversations v2).
// The portfolio panel sends its own value ('portfolio') from ITS own hook.
export const ASK_AICA_SOURCE_LEAD = 'lead';

/** Assistant-row `status` values Athena persists (messages.status). */
export const MESSAGE_STATUS = {
    RUNNING: 'running',
    CANCELLING: 'cancelling',
    PARTIAL: 'partial',
    COMPLETE: 'complete',
    ERROR: 'error',
} as const;

/**
 * A turn whose answer is still being produced server-side: the composer stays
 * blocked, the working state renders, and a reloaded page re-attaches to it.
 *
 * `streaming` is deliberately ABSENT. It is the discriminator that keeps the
 * legacy rows inert — they are finished answers wearing a stale label, so
 * including it would put ~1.8k conversations into a working state that never
 * ends and open a re-attach stream for a run that finished months ago.
 */
export const IN_PROGRESS_MESSAGE_STATUSES: string[] = [MESSAGE_STATUS.RUNNING, MESSAGE_STATUS.CANCELLING];

export const isInProgressStatus = (status?: string): boolean => !!status && IN_PROGRESS_MESSAGE_STATUSES.includes(status);

/** Re-attach reconnect backoff, in ms; the last entry is the cap. */
export const REATTACH_BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];

/** Wall-clock bound on reconnecting to one run (15 min). Exhaustion is NOT a
 * failure — the working state and Stop both stay, only the copy changes. */
export const REATTACH_MAX_WALL_MS = 900000;

/** Safety-fallback bound after a server-accepted Stop (RECUR-28136): once
 * `/chat/stop` returns 2xx, the client leaves its stream open rather than
 * aborting so Athena's cancellation teardown can push a synthetic final frame
 * down it. That frame is queued synchronously server-side, so it is a network
 * round-trip away, not tied to LLM generation time — 10s is generous slack for
 * that flush without leaving the composer blocked on a dropped response. */
export const STOP_SETTLE_FALLBACK_MS = 10000;

/** The three distinct in-progress strings. None substitutes for another. */
export const ASK_AICA_WORKING_COPY = {
    /** In progress with no text yet — the promise has to be stated in words. */
    LEAVE_SAFE: 'Still working on this. You can close this or come back later — the answer will be here.',
    /** Reconnect exhausted while the run is still in progress. */
    EXHAUSTED: 'Still working — reopen this panel to check again.',
    /** An empty turn that was stopped by the user. */
    STOPPED: 'Generation stopped.',
} as const;

/** Per-question states of a clarifying-question round (metadata.clarification.questions_state). */
export const CLARIFICATION_STATE = {
    PENDING: 'pending',
    ANSWERED: 'answered',
    IN_CHAT: 'in_chat',
    EXPIRED: 'expired',
} as const;

/** `cancel_reason` of a cancelled round — also what a locally-settled expiry is stamped with. */
export const CLARIFICATION_CANCEL_REASON = {
    STOP: 'stop',
    NEW_TURN: 'new_turn',
} as const;

/** `clarification_resolution.mode` — how a round ended. */
export const CLARIFICATION_RESOLUTION_MODE = {
    ANSWER: 'answer',
    CANCELLED: 'cancelled',
} as const;

/** The always-last row of every question. The FE owns it: the model never authors or renames it. */
export const TYPE_ANSWER_LABEL = 'Type answer';

/** Athena's `error` code on a 409 / stale final frame for an already-settled or foreign round. */
export const CLARIFICATION_STALE_ERROR = 'clarification_stale';

/** `query_source` Athena stamps on the user row that answers a clarification card. The row stays
 * server-side (it anchors the turn); the FE hides its redundant bubble — the settled note carries the Q&A. */
export const CLARIFICATION_REPLY_QUERY_SOURCE = 'clarification_reply';

/** Mirrors the server's per-answer text bound (1-500 chars). */
export const CLARIFICATION_TYPED_ANSWER_MAX_LENGTH = 500;

export const CLARIFICATION_COPY = {
    RECOMMENDED: 'Recommended',
    ENTER: 'Enter',
    PENDING_LINE: 'Waiting for your answer',
    SENT_LINE: 'Answer sent',
    YOU_PICKED: 'you picked',
    YOU_TYPED: 'you typed',
    NOT_ANSWERED: 'not answered',
    AWAITING_ANSWER: 'awaiting answer',
    CANCEL_STOP: 'you stopped',
    CANCEL_NEW_TURN: 'you moved on',
    PREVIOUS_QUESTION: 'Previous question',
    NEXT_QUESTION: 'Next question',
} as const;
